import type { ButtonHTMLAttributes } from 'react';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'md' | 'sm';

const BASE =
  'inline-flex min-h-11 items-center justify-center gap-2 rounded-xl font-semibold transition-[background-color,border-color,box-shadow,transform,color] duration-150 ease-out active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50 disabled:active:scale-100';

const VARIANTS: Record<ButtonVariant, string> = {
  // White text only on teal (brand contrast rule).
  primary: 'bg-brand text-white shadow-sm shadow-brand/25 hover:bg-brand-dark hover:shadow-md',
  secondary:
    'border border-line bg-surface text-brand shadow-sm shadow-brand/5 hover:border-brand/40 hover:bg-brand-soft',
  ghost: 'text-brand hover:bg-brand-soft',
  danger: 'border border-fail/40 bg-surface text-fail hover:border-fail hover:bg-red-50',
};

const SIZES: Record<ButtonSize, string> = {
  md: 'px-4 py-2 text-base',
  sm: 'px-3 py-1.5 text-sm',
};

/**
 * Class string for anything that should look like a button (e.g. a router
 * `<Link>`). `block` (default true) stretches it to the container width,
 * matching `<Button>`'s historical full-width layout.
 */
export function buttonClass({
  variant = 'primary',
  size = 'md',
  block = true,
}: { variant?: ButtonVariant; size?: ButtonSize; block?: boolean } = {}): string {
  return `${BASE} ${VARIANTS[variant]} ${SIZES[size]} ${block ? 'w-full' : ''}`;
}

export function Button({
  variant = 'primary',
  size = 'md',
  block = true,
  className = '',
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  block?: boolean;
}) {
  return <button className={`${buttonClass({ variant, size, block })} ${className}`} {...rest} />;
}
